import type { ReactNode } from 'react'
import { Clock, Mail, Megaphone, Pause, Pencil, Play, Trash2, Webhook } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import {
  WhatsAppIcon, SlackIcon, TeamsIcon, DiscordIcon,
} from '@/frontend/components/DigitalWorkers/channelIcons'
import type { CronJob } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { formatSchedule, formatTimestamp } from './rotinasFormatters'
import RoutineDeliveryStatus from './RoutineDeliveryStatus'

interface Props {
  jobs: CronJob[]
  canManage?: boolean
  onEdit?: (job: CronJob) => void
  onDelete?: (job: CronJob) => void
  onToggleEnabled?: (job: CronJob) => void
}

interface JobStatus {
  dot: string
  text: string
  bg: string
  label: string
}

// Mirrors the error rule in RotinasTab.tsx and the Ruby cron_job_in_error? check
// so the dot, the filters, and the backend stay consistent.
function jobStatus(job: CronJob): JobStatus {
  const inError = (job.state.consecutiveErrors ?? 0) > 0 || job.state.lastRunStatus === 'error'
  if (inError) {
    return { dot: 'bg-destructive', text: 'text-destructive', bg: 'bg-destructive/12', label: 'Com erro' }
  }
  if (job.enabled) {
    return { dot: 'bg-success', text: 'text-success', bg: 'bg-success/12', label: 'Ativa' }
  }
  return { dot: 'bg-muted-foreground/50', text: 'text-muted-foreground', bg: 'bg-muted', label: 'Pausada' }
}

function isToday(ms: number): boolean {
  return new Date(ms).toDateString() === new Date().toDateString()
}

function formatTimeOfDay(ms: number): string {
  return new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

interface DeliveryChip {
  icon: ReactNode
  label: string
  tooltip?: string
}

const ROUTINE_DEFINED_DELIVERY_TOOLTIP = 'A rotina não usa entrega automática do Gateway; o envio pode ser definido nas instruções da mensagem.'

// Anatomia de th do DS (Iteração 1): 11/650 uppercase baixa.
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground'

// Maps a job's delivery target to a small icon chip.
function deliveryChip(job: CronJob): DeliveryChip {
  const delivery = job.delivery
  if (!delivery) {
    return {
      icon: <Megaphone className="h-3 w-3" />,
      label: 'Definida na rotina',
      tooltip: ROUTINE_DEFINED_DELIVERY_TOOLTIP,
    }
  }
  if (delivery.mode === 'none') {
    return {
      icon: <Megaphone className="h-3 w-3" />,
      label: 'Definida na rotina',
      tooltip: ROUTINE_DEFINED_DELIVERY_TOOLTIP,
    }
  }
  if (delivery.mode === 'webhook') return { icon: <Webhook className="h-3 w-3" />, label: 'Webhook' }

  const channel = delivery.channel
  if (channel) {
    const c = channel.toLowerCase()
    if (c.includes('slack')) return { icon: <SlackIcon />, label: channel }
    if (c.includes('whatsapp')) return { icon: <WhatsAppIcon />, label: channel }
    if (c.includes('teams')) return { icon: <TeamsIcon />, label: channel }
    if (c.includes('discord')) return { icon: <DiscordIcon />, label: channel }
    if (c.includes('email') || c.includes('mail')) return { icon: <Mail className="h-3 w-3" />, label: channel }
    return { icon: <Megaphone className="h-3 w-3" />, label: channel }
  }
  return { icon: <Megaphone className="h-3 w-3" />, label: 'Anunciar' }
}

export default function RotinasJobsTable({ jobs, canManage, onEdit, onDelete, onToggleEnabled }: Props) {
  return (
    <Table className="min-w-[720px] table-fixed text-[13px]">
      <TableHeader>
        <TableRow>
          <TableHead className={TH_CLS}>Rotina</TableHead>
          <TableHead className={`w-48 ${TH_CLS}`}>Agendamento</TableHead>
          <TableHead className={`w-40 ${TH_CLS}`}>Canal</TableHead>
          {canManage && <TableHead className={`w-36 text-right ${TH_CLS}`}>Ações</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.map((job) => {
          const status = jobStatus(job)
          const chip = deliveryChip(job)
          const nextRunMs = job.state.nextRunAtMs
          const deliveryChipContent = (
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-secondary px-2 py-1 text-[12px] text-muted-foreground">
              <span className="shrink-0">{chip.icon}</span>
              <span className="truncate">{chip.label}</span>
            </span>
          )

          return (
            <TableRow key={job.id}>
              <TableCell>
                {/* Title + subtle status dot-pill (description moves to the tooltip) */}
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="min-w-0 truncate font-medium text-foreground">{job.name}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs whitespace-normal break-words">
                      <p>{job.name}</p>
                      {job.description && <p className="mt-1 text-xs opacity-80">{job.description}</p>}
                    </TooltipContent>
                  </Tooltip>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-[9px] py-0.5 text-[11px] font-semibold ${status.bg} ${status.text}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} aria-hidden="true" />
                    {status.label}
                  </span>
                </div>
              </TableCell>

              <TableCell>
                <div className="flex items-start gap-2">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate text-[13px] text-foreground">{formatSchedule(job)}</div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      Próxima:{' '}
                      {nextRunMs ? (
                        <span className="font-mono text-[11.5px] text-foreground">
                          {isToday(nextRunMs) ? `Hoje, ${formatTimeOfDay(nextRunMs)}` : formatTimestamp(nextRunMs)}
                        </span>
                      ) : (
                        'Sem próxima execução'
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>

              <TableCell>
                {chip.tooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {deliveryChipContent}
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs whitespace-normal break-words">
                      {chip.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  deliveryChipContent
                )}
                <RoutineDeliveryStatus state={job.state} />
              </TableCell>

              {canManage && (
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon-sm"
                          aria-label={job.enabled ? 'Pausar' : 'Reativar'}
                          onClick={() => onToggleEnabled?.(job)}
                        >
                          {job.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{job.enabled ? 'Pausar' : 'Reativar'}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button type="button" variant="outline" size="icon-sm" aria-label="Editar" onClick={() => onEdit?.(job)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Editar</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          aria-label="Excluir"
                          onClick={() => onDelete?.(job)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Excluir</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
